import { useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import type { ScreenerQuestion } from '@cvx/features/jobs/screener_questions_db'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TranslateButton } from '@/features/jobs/components/translate-button'

type QuestionType = 'single_select' | 'multi_choice'

type ScreenerQuestionDraft = {
  readonly id: string
  text: string
  type: QuestionType
  options: string[]
  disqualifyingAnswers: string[]
  textTranslation: string
  textTranslationIsDraft: boolean
}

const createEmptyScreenerQuestion = (): ScreenerQuestionDraft => ({
  id: crypto.randomUUID(),
  text: '',
  type: 'single_select',
  options: ['', ''],
  disqualifyingAnswers: [],
  textTranslation: '',
  textTranslationIsDraft: false,
})

const fromQuestions = (
  questions: readonly ScreenerQuestion[],
): ScreenerQuestionDraft[] =>
  questions.map((q) => ({
    id: q._id,
    text: q.text,
    type: q.type,
    options: [...q.options],
    disqualifyingAnswers: [...q.disqualifyingAnswers],
    textTranslation: q.textTranslations?.['en'] ?? '',
    textTranslationIsDraft: false,
  }))

type SortableScreenerQuestionProps = {
  readonly jobId: Id<'jobs'>
  readonly jobLanguage: string
  readonly question: ScreenerQuestionDraft
  readonly index: number
  readonly isLocked: boolean
  readonly onUpdate: (id: string, patch: Partial<ScreenerQuestionDraft>) => void
  readonly onRemove: (id: string) => void
}

const SortableScreenerQuestion = ({
  jobId,
  jobLanguage,
  question,
  index,
  isLocked,
  onUpdate,
  onRemove,
}: SortableScreenerQuestionProps) => {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const updateOption = (optionIndex: number, value: string) => {
    const updated = question.options.map((o, i) =>
      i === optionIndex ? value : o,
    )
    onUpdate(question.id, { options: updated })
  }

  const addOption = () => {
    onUpdate(question.id, { options: [...question.options, ''] })
  }

  const removeOption = (optionIndex: number) => {
    const updatedOptions = question.options.filter((_, i) => i !== optionIndex)
    const removedOption = question.options[optionIndex] ?? ''
    const updatedDisqualifying = question.disqualifyingAnswers.filter(
      (a) => a !== removedOption,
    )
    onUpdate(question.id, {
      options: updatedOptions,
      disqualifyingAnswers: updatedDisqualifying,
    })
  }

  const toggleDisqualifying = (option: string) => {
    const isDisqualifying = question.disqualifyingAnswers.includes(option)
    onUpdate(question.id, {
      disqualifyingAnswers: isDisqualifying
        ? question.disqualifyingAnswers.filter((a) => a !== option)
        : [...question.disqualifyingAnswers, option],
    })
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border border-border bg-background p-4',
        isDragging && 'opacity-50',
      )}
    >
      <div className="flex gap-3">
        <button
          type="button"
          className={cn(
            'mt-2 touch-none text-primary/30',
            isLocked
              ? 'cursor-not-allowed'
              : 'cursor-grab hover:text-primary/60 active:cursor-grabbing',
          )}
          {...attributes}
          {...(isLocked ? {} : listeners)}
          aria-label={t('jobs.screener.drag')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <circle cx="9" cy="6" r="1.5" />
            <circle cx="15" cy="6" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="9" cy="18" r="1.5" />
            <circle cx="15" cy="18" r="1.5" />
          </svg>
        </button>

        <div className="flex flex-1 flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-xs font-medium text-primary/40">
              {index + 1}.
            </span>
            <Input
              value={question.text}
              onChange={(e) => onUpdate(question.id, { text: e.target.value })}
              placeholder={t('jobs.screener.questionPlaceholder')}
              className="flex-1"
              disabled={isLocked}
            />
            <select
              value={question.type}
              onChange={(e) =>
                onUpdate(question.id, { type: e.target.value as QuestionType })
              }
              className="h-9 rounded-md border border-input bg-background px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isLocked}
            >
              <option value="single_select">
                {t('jobs.screener.singleSelect')}
              </option>
              <option value="multi_choice">
                {t('jobs.screener.multiChoice')}
              </option>
            </select>
          </div>

          {jobLanguage === 'both' && (
            <div className="flex flex-col gap-1.5 pl-5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-primary/50">
                  {t('jobs.translations.english')}
                </span>
                <TranslateButton
                  jobId={jobId}
                  sourceLanguage="sv"
                  targetLanguage="en"
                  text={question.text}
                  isHtml={false}
                  disabled={isLocked || !question.text.trim()}
                  onTranslated={(text) =>
                    onUpdate(question.id, {
                      textTranslation: text,
                      textTranslationIsDraft: true,
                    })
                  }
                />
              </div>
              {question.textTranslationIsDraft && (
                <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
                  {t('jobs.translations.draftHint')}
                </div>
              )}
              <Input
                value={question.textTranslation}
                onChange={(e) =>
                  onUpdate(question.id, { textTranslation: e.target.value })
                }
                placeholder={t('jobs.screener.questionPlaceholder')}
                className={
                  question.textTranslationIsDraft
                    ? 'border-amber-400 ring-1 ring-amber-400/30'
                    : ''
                }
                disabled={isLocked}
              />
            </div>
          )}

          <div className="flex flex-col gap-2 pl-5">
            {question.options.map((option, optIndex) => (
              <div key={optIndex} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={question.disqualifyingAnswers.includes(option)}
                  onChange={() => toggleDisqualifying(option)}
                  title={t('jobs.screener.markDisqualifying')}
                  className="size-4 shrink-0 cursor-pointer accent-destructive disabled:cursor-not-allowed"
                  disabled={isLocked}
                />
                <Input
                  value={option}
                  onChange={(e) => updateOption(optIndex, e.target.value)}
                  placeholder={`${t('jobs.screener.optionPlaceholder')} ${optIndex + 1}`}
                  className="flex-1"
                  disabled={isLocked}
                />
                {!isLocked && question.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(optIndex)}
                    className="shrink-0 text-destructive/60 hover:text-destructive"
                    aria-label={t('jobs.screener.removeOption')}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <p className="text-xs text-primary/40">
              {t('jobs.screener.disqualifyingHint')}
            </p>
            {!isLocked && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addOption}
              >
                {t('jobs.screener.addOption')}
              </Button>
            )}
          </div>

          {!isLocked && (
            <button
              type="button"
              onClick={() => onRemove(question.id)}
              className="self-start text-xs text-destructive/70 hover:text-destructive"
            >
              {t('jobs.screener.remove')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

type ScreenerQuestionsEditorProps = {
  readonly jobId: Id<'jobs'>
  readonly jobLanguage: string
  readonly initialQuestions: readonly ScreenerQuestion[]
  readonly isLocked: boolean
}

export const ScreenerQuestionsEditor = ({
  jobId,
  jobLanguage,
  initialQuestions,
  isLocked,
}: ScreenerQuestionsEditorProps) => {
  const { t } = useTranslation()
  const [questions, setQuestions] = useState<ScreenerQuestionDraft[]>(() =>
    fromQuestions(initialQuestions),
  )
  const [saved, setSaved] = useState(false)

  const { mutateAsync: saveQuestions, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.jobs.upsertMyScreenerQuestions,
    ),
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setQuestions((prev) => {
      const oldIndex = prev.findIndex((q) => q.id === active.id)
      const newIndex = prev.findIndex((q) => q.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const handleUpdate = (id: string, patch: Partial<ScreenerQuestionDraft>) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    )
  }

  const handleRemove = (id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id))
  }

  const handleSave = async () => {
    await saveQuestions({
      jobId,
      questions: questions.map((q, i) => ({
        text: q.text,
        type: q.type,
        options: q.options.filter((o) => o.trim() !== ''),
        disqualifyingAnswers: q.disqualifyingAnswers.filter(
          (a) => a.trim() !== '',
        ),
        order: i,
        textTranslations: q.textTranslation
          ? { en: q.textTranslation }
          : undefined,
      })),
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="flex flex-col gap-4">
      {isLocked && (
        <div className="flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800 dark:border-orange-800/40 dark:bg-orange-900/20 dark:text-orange-400">
          <span>🔒</span>
          <span>{t('jobs.locked.notice')}</span>
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={questions.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          {questions.map((question, index) => (
            <SortableScreenerQuestion
              key={question.id}
              jobId={jobId}
              jobLanguage={jobLanguage}
              question={question}
              index={index}
              isLocked={isLocked}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
            />
          ))}
        </SortableContext>
      </DndContext>

      {questions.length === 0 && (
        <p className="py-8 text-center text-sm text-primary/40">
          {t('jobs.screener.empty')}
        </p>
      )}

      {!isLocked && (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setQuestions((prev) => [...prev, createEmptyScreenerQuestion()])
            }
          >
            {t('jobs.screener.addQuestion')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={isPending}>
            {isPending ? t('jobs.screener.saving') : t('jobs.screener.save')}
          </Button>
          {saved && (
            <span className="text-sm text-primary/60">
              {t('jobs.screener.saved')}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
